import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createInstructions, deleteInstruction, getInstructionsById, InstructionsLoadingKeys } from '../../slices/instructionsSlice';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { toast } from 'react-toastify';
import DraggableRow from '../../components/common/DraggableRow';

const ProductInstructions = ({ product, access }) => {
  const {
    instructions,
    loadingKeys: instructionsLoadingKeys,
  } = useSelector((state) => state.instructions);

  const [items, setItems] = useState([]);
  const [modifiedItems, setModifiedItems] = useState(null);
  const textareaRefs = React.useRef({});
  const dispatch = useDispatch();

  useEffect(() => {
    if (product?.id) {
      dispatch(getInstructionsById({ id: product.id }));
    }
  }, [dispatch, product?.id]);

  useEffect(() => {
    if (Array.isArray(instructions)) {
      setItems([
        ...instructions,
        { title: '', description: '', product_id: product?.id, priority: instructions.length + 1 }
      ]);
      setModifiedItems(null); // Reset modified items when instructions change
    }
  }, [instructions, product?.id]);

  const moveRow = (fromIndex, toIndex) => {
    if (access === "edit") {
      const itemsToModify = modifiedItems || items;
      const newItems = [...itemsToModify];
      const [movedItem] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, movedItem);
      
      // Update priorities
      const updatedItems = newItems.map((item, index) => {
        if (index === newItems.length - 1 && !item.id && !item.title && !item.description) {
          return item;
        }
        return {
          ...item,
          priority: index + 1
        };
      });
      
      setModifiedItems(updatedItems);
    }
  };

  const handleUpdateItem = (index, updatedItem) => {
    if (access === "edit") {
      const itemsToModify = modifiedItems || items;
      const newItems = [...itemsToModify];
      newItems[index] = updatedItem;
      if (itemsToModify.length === index + 1) {
        newItems.push({ title: '', description: '', product_id: product?.id, priority: itemsToModify.length + 1 });
      }
      setModifiedItems(newItems);
    }
  };

  const handleDeleteItem = (itemId, itemIndex) => {
    if (access === "edit") {
      if (itemId) {
        dispatch(deleteInstruction({ id: product.id, idToDelete: itemId }))
          .then(() => {
            toast.success("Instruction removed successfully.", { position: "bottom-right" });
          })
          .catch((error) => {
            toast.error(`Failed to remove instruction: ${error.message}`, { position: "bottom-right" });
          });
      } else {
        const itemsToModify = modifiedItems || items;
        const updatedItems = itemsToModify.filter((_, index) => index !== itemIndex);
        setModifiedItems(updatedItems);
      }
    }
  }

  const handleSave = () => {
    if (access === "edit") {
      if (modifiedItems) {
        dispatch(createInstructions({
          creationData: {
            id: product?.id,
            newInstructions: modifiedItems.filter((item) => item.title || item.description)
          }
        }))
          .then(() => {
            toast.success("Instructions saved successfully.", { position: "bottom-right" });
            setModifiedItems(null); // Reset modified items after save
          })
          .catch((error) => {
            toast.error(`Failed to save instructions: ${error.message}`, { position: "bottom-right" });
          });
      }
    }
  };

  // Use modifiedItems if they exist, otherwise use items
  const displayItems = modifiedItems || items;

  const instructionsIsLoading = () => 
    instructionsLoadingKeys.includes(InstructionsLoadingKeys.getInstructions) ||
    instructionsLoadingKeys.includes(InstructionsLoadingKeys.create) ||
    instructionsLoadingKeys.includes(InstructionsLoadingKeys.delete);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`mt-6`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Product Instructions</h3>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto relative">
          {instructionsIsLoading() && <OverlayGrayBackground />}
          <table className="w-full table-fixed">
            <colgroup>
              {access === "edit" && (
                <col className="w-[5%]" />
              )}
              {/* <col className="w-[25%]" /> */}
              <col className="w-[90%]" />
              {access === "edit" && (
                <col className="w-[5%]" />
              )}
            </colgroup>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {access === "edit" && (
                  <th className="py-3 px-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sr-only"></th>
                )}
                {/* <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instruction</th> */}
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                {access === "edit" && (
                  <th className="py-3 px-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sr-only"></th>
                )}
              </tr>
            </thead>
            <tbody>
              {displayItems.map((item, index) => (
                <DraggableRow
                  key={`${item.id}_${index}`}
                  rowType="instructions"
                  item={item}
                  access={access}
                  index={index}
                  isLastElement={displayItems.length === index + 1}
                  dataIsLoading={instructionsIsLoading()}
                  moveRow={moveRow}
                  handleUpdateItem={handleUpdateItem}
                  handleDeleteItem={handleDeleteItem}
                  textareaRefs={textareaRefs}
                  totalRows={displayItems.length}
                />
              ))}
            </tbody>
          </table>
        </div>
        {access === "edit" && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={handleSave}
              disabled={!modifiedItems}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white ${
                modifiedItems ? 'bg-primary hover:bg-gray-700' : 'bg-gray-400 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700`}
              aria-label="Save instructions"
              tabIndex={0}
            >
              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save
            </button>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default ProductInstructions; 